import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wind, ArrowLeft } from "lucide-react";
import { SEBA_IMG } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Qualcosa è andato storto.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("seba@meteoseba.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-gradient-to-br from-sky-700 to-cyan-600 overflow-hidden">
        <img src={SEBA_IMG} alt="Seba" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-end text-white">
          <Wind className="h-12 w-12 mb-4" />
          <h2 className="font-head text-4xl font-extrabold">Meteo Torbole Live</h2>
          <p className="text-white/80 mt-2 max-w-sm">
            Accedi per pubblicare aggiornamenti, condizioni e foto dal campo.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-sky-50">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600 mb-8" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Torna alla dashboard
          </Link>
          <div className="h-11 w-11 rounded-2xl bg-sky-600 flex items-center justify-center mb-5">
            <Wind className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-head text-3xl font-extrabold text-slate-900">Area Seba</h1>
          <p className="text-slate-500 mb-6">Accesso riservato all'amministratore.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 rounded-xl h-11"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 rounded-xl h-11"
                data-testid="login-password"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-sky-600 hover:bg-sky-700 font-head font-semibold text-base"
              data-testid="login-submit"
            >
              {loading ? "Accesso..." : "Accedi"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
